import React, { useState, useRef, useEffect } from "react";

// import css
import "./Mid-Section-Pc.scss";

//import icons
import { CiEdit } from "react-icons/ci";
import { RiLockPasswordFill } from "react-icons/ri";

//imports for user data
import { useSelector, useDispatch } from "react-redux";

//functions for datat fetching
import {
  getProfile,
  editProfile,
  changeUserPassword,
  editProfileImage, // <-- import the thunk for image upload
} from "../../../../redux/slices/sliceActions";

//custom toast
import customToasts from "../../../assets/reusableComponents/myToasts";
import { ToastContainer, Bounce } from "react-toastify";

const MidSectionProfilePc = ({
  resumeTourAtStep,
  setSharedValue,
  isTourActive,
}) => {
  //data fetched from the store
  const dispatch = useDispatch();
  const { user, profile } = useSelector((state) => state.landlordslice);
  // Fix extraction of id and token based on user object structure
  const token = user?.accessToken;
  const id = user?.data?.tenantId;

  const [profileUpdated, setProfileUpdated] = useState(false);

  useEffect(() => {
    console.log("user", id);
    if (id) {
      dispatch(getProfile({ userId: id })).then(() => {
        setProfileUpdated(true);
      });
    }
  }, [id]);

  //input values
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [profilePicture, setProfilePicture] = useState(null);

  // Phone number validation state
  const [phoneError, setPhoneError] = useState("");

  // Track selected new profile picture and its preview
  const [selectedProfilePic, setSelectedProfilePic] = useState(null);
  const [selectedProfilePicPreview, setSelectedProfilePicPreview] =
    useState(null);

  useEffect(() => {
    if (profileUpdated && profile.user_name) {
      const { firstName, lastName } = getFirstAndLastName(profile.user_name);
      setFirstName(firstName);
      setLastName(lastName);
      setEmail(profile.user_email || "");
      setPhoneNumber(profile.user_phone_number || "");
      setProfilePicture(profile.profile_picture || null);
      setSelectedProfilePic(null);
      setSelectedProfilePicPreview(null);
    }
  }, [profileUpdated]);

  const [activeItem, setActiveItem] = useState("Profile");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeletePicModal, setShowDeletePicModal] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  //function to handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "phoneNumber":
        setPhoneError("");
        setPhoneNumber(value);
        break;
      case "oldPassword":
        setOldPassword(value);
        break;
      case "newPassword":
        setNewPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
      default:
        break;
    }
  };

  const handleNavItemClick = (item) => {
    setActiveItem(item);
  };

  //profile picture
  const fileInputRef = useRef(null);
  const handleChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedProfilePic(file);
      setSelectedProfilePicPreview(URL.createObjectURL(file));
    }
  };

  //focus
  const [focus, setFocus] = useState({});

  const handleFocus = (inputName) => {
    setFocus({ ...focus, [inputName]: true });
  };

  const handleBlur = (inputName) => {
    setFocus({ ...focus, [inputName]: false });
  };

  //function to handle profile update
  const handleProfileUpdate = () => {
    // Validate phone number before submitting
    if (phoneError) {
      customToasts.error("Please enter a valid Kenyan phone number.");
      return;
    }

    const fullName = `${firstName} ${lastName}`.trim();

    // Dispatch profile info update
    customToasts.promise(
      dispatch(
        editProfile({
          name: fullName,
          email,
          phone: `+254${phoneNumber.substring(1)}`, // Convert to international format
        })
      ).unwrap(),
      {
        pending: "Updating profile...",
        success: "Profile updated successfully",
        error: "Failed to update profile, please try again or contact support",
      }
    );

    // If a new profile picture is selected, dispatch its upload using editProfileImage
    if (selectedProfilePic) {
      customToasts.promise(
        dispatch(
          editProfileImage({ id, token, image: selectedProfilePic })
        ).unwrap(),
        {
          pending: "Uploading profile picture...",
          success: "Profile picture updated successfully",
          error:
            "Failed to update profile picture, please try again or contact support",
        }
      );
    }

    setShowConfirmModal(false);
  };

  const handleConfirmChanges = () => {
    // Validate Kenyan phone number format: starts with 07 or 01 and 10 digits
    if (/^(07\d{8}|01\d{8})$/.test(phoneNumber)) {
      setPhoneError("");
    } else {
      setPhoneError(
        "Phone number must be in the format 07XXXXXXXX or 01XXXXXXXX"
      );
      customToasts.error("Please enter a valid Kenyan phone number.");
      return;
    }

    setShowConfirmModal(true);
  };

  const handleCancelModal = () => {
    setShowConfirmModal(false);
  };

  const updatePasswordFunction = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      customToasts.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      customToasts.error("New password and confirm password do not match");
      return;
    }
    if (oldPassword === newPassword) {
      customToasts.error("Old password and new password cannot be the same");
      return;
    }

    customToasts
      .promise(
        dispatch(changeUserPassword({ oldPassword, newPassword })).unwrap(),
        {
          pending: "Updating password...",
          success: "Password updated successfully",
          error: {
            render({ data }) {
              // Use the error message from the response if it exists
              return data?.response?.data?.message || "An error occurred";
            },
          },
        },
        {
          isPasswordReset: true, // This is the correct way to pass the option
        }
      )
      .then(() => {
        // Clear password fields
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");

        // If tour is active, resume at step 2 (overview) after password update
        if (isTourActive && resumeTourAtStep && setSharedValue) {
          setTimeout(() => {
            setSharedValue("Overview");
            resumeTourAtStep(2);
          }, 2000); // Give user time to see success message
        }
      })
      .catch(() => {
        // Error is already handled by customToasts.promise
      });
  };

  const handleCancelPasswordChange = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleDeletePicture = () => {
    // Show confirmation modal before actually deleting
    setShowDeletePicModal(true);
  };

  const handleConfirmDeletePicture = () => {
    if (profilePicture || selectedProfilePicPreview) {
      dispatch(editProfileImage({ remove: true }));
    }
    setSelectedProfilePic(null);
    setSelectedProfilePicPreview(null);
    setProfilePicture(null);
    setShowDeletePicModal(false);
  };

  const handleCancelDeletePicture = () => {
    setShowDeletePicModal(false);
  };

  return (
    <div className="profile-pc">
      <div className="left-side-profile">
        <div
          className={`choice ${activeItem === "Profile" ? "active" : ""}`}
          onClick={() => handleNavItemClick("Profile")}
        >
          <div className="icon-and-title">
            <div className="icon">
              <CiEdit fontSize={25} />
            </div>
            <div className="title">Edit Profile</div>
          </div>
          <div className="active-shape"></div>
        </div>
        <div
          className={`choice ${activeItem === "Password" ? "active" : ""}`}
          onClick={() => handleNavItemClick("Password")}
        >
          <div className="icon-and-title">
            <div className="icon">
              <RiLockPasswordFill fontSize={25} />
            </div>
            <div className="title">Change Password</div>
          </div>
          <div className="active-shape"></div>
        </div>
      </div>
      <div className="right-side-profile">
        {activeItem === "Profile" && (
          <>
            <div className="edit-profile-page">
              <div className="top-half">
                <div className="title">Edit Profile</div>
                <div className="profile-pic-container">
                  {selectedProfilePicPreview || profilePicture ? (
                    <>
                      <img
                        src={selectedProfilePicPreview || profilePicture}
                        alt="profile picture"
                        className="profile-picture"
                      />
                    </>
                  ) : (
                    <div className="profile-pic"></div>
                  )}

                  <div className="buttons-containers">
                    <div className="upload-button">
                      Change Picture
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: "block" }}
                        onChange={handleChange}
                      />
                    </div>
                    <div
                      className="upload-button"
                      onClick={handleDeletePicture}
                    >
                      Delete Picture
                    </div>
                  </div>
                </div>
              </div>
              <div className="bottom-half">
                <div className="fields-halves">
                  <div className="individual-half">
                    <label
                      htmlFor="first-name-input"
                      className={`text ${
                        focus["first-name-input"] ? "focus" : ""
                      }`}
                    >
                      First Name:
                    </label>
                    <input
                      type="text"
                      id="first-name-input"
                      value={firstName}
                      name="firstName"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("first-name-input")}
                      onBlur={() => handleBlur("first-name-input")}
                      onChange={handleInputChange}
                      placeholder="First Name eg John"
                    />
                  </div>
                  <div className="individual-half">
                    <label
                      htmlFor="last-name-input"
                      className={`text ${
                        focus["last-name-input"] ? "focus" : ""
                      }`}
                    >
                      Last Name:
                    </label>
                    <input
                      type="text"
                      id="last-name-input"
                      value={lastName}
                      name="lastName"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("last-name-input")}
                      onBlur={() => handleBlur("last-name-input")}
                      onChange={handleInputChange}
                      placeholder="Last Name eg Doe"
                    />
                  </div>
                </div>

                <div className="fields-halves">
                  <div className={`individual-half margin-top`}>
                    <label
                      htmlFor="email-input"
                      className={`text ${focus["email-input"] ? "focus" : ""}`}
                    >
                      email
                    </label>
                    <input
                      type="text"
                      id="email-input"
                      value={email}
                      name="email"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("email-input")}
                      onBlur={() => handleBlur("email-input")}
                      onChange={handleInputChange}
                      placeholder="Email address eg example@example.com"
                    />
                  </div>
                </div>
                <div className="fields-halves">
                  <div className={`individual-half margin-top`}>
                    <label
                      htmlFor="phone-number-input"
                      className={`text ${
                        focus["phone-number-input"] ? "focus" : ""
                      }`}
                    >
                      Phone Number
                    </label>
                    <input
                      type="text"
                      id="phone-number-input"
                      value={phoneNumber}
                      placeholder="Phone Number eg 07XXXXXXXX or 01XXXXXXXX"
                      name="phoneNumber"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("phone-number-input")}
                      onBlur={() => handleBlur("phone-number-input")}
                      onChange={handleInputChange}
                    />
                    {phoneError && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "0.9rem",
                          marginTop: "0.3rem",
                        }}
                      >
                        {phoneError}
                      </div>
                    )}
                  </div>
                </div>

                <div className={"buttons-container "}>
                  <div className="upload-button" onClick={handleConfirmChanges}>
                    Confirm Changes
                  </div>
                  <div className="upload-button">Cancel Changes</div>
                </div>
              </div>
            </div>
          </>
        )}
        {activeItem === "Password" && (
          <>
            <div className="change-password-page">
              <div className="top-half">
                <div className="title">Change Password</div>
                <div className="sub-title">
                  Change your password by entering the old password first, then
                  the new one
                </div>
              </div>
              <div className="bottom-half">
                <div className="fields-halves">
                  <div className={`individual-half margin-top`}>
                    <label
                      htmlFor="old-password-input"
                      className={`text ${
                        focus["old-password-input"] ? "focus" : ""
                      }`}
                    >
                      Old Password
                    </label>
                    <input
                      type="password"
                      id="old-password-input"
                      placeholder="Enter old password"
                      name="oldPassword"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("old-password-input")}
                      onBlur={() => handleBlur("old-password-input")}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="fields-halves">
                  <div className={`individual-half margin-top`}>
                    <label
                      htmlFor="new-password-input"
                      className={`text ${
                        focus["new-password-input"] ? "focus" : ""
                      }`}
                    >
                      New Password
                    </label>
                    <input
                      type="password"
                      id="new-password-input"
                      placeholder="Enter new password"
                      name="newPassword"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("new-password-input")}
                      onBlur={() => handleBlur("new-password-input")}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="fields-halves">
                  <div className={`individual-half margin-top`}>
                    <label
                      htmlFor="confirm-password-input"
                      className={`text ${
                        focus["confirm-password-input"] ? "focus" : ""
                      }`}
                    >
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirm-password-input"
                      placeholder="Confirm new password"
                      name="confirmPassword"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("confirm-password-input")}
                      onBlur={() => handleBlur("confirm-password-input")}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className={"buttons-container "}>
                  <div
                    className="upload-button"
                    onClick={updatePasswordFunction}
                  >
                    Confirm Changes
                  </div>
                  <div
                    className="upload-button"
                    onClick={handleCancelPasswordChange}
                  >
                    Cancel Changes
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="title">Confirm Profile Changes</div>
            <div className="sub-title">
              Are you sure you want to update your profile with the following
              information?
            </div>
            <div className="modal-details">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "1rem",
                }}
              >
                {selectedProfilePicPreview || profilePicture ? (
                  <img
                    src={selectedProfilePicPreview || profilePicture}
                    alt="profile preview"
                    className="profile-picture"
                    style={{ width: 60, height: 60, borderRadius: "50%" }}
                  />
                ) : (
                  <div
                    className="profile-pic"
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      background: "#192a3a",
                    }}
                  ></div>
                )}
                <span>Profile Picture</span>
              </div>
              <div className="sub-title">
                <strong>Name:</strong> {firstName} {lastName}
              </div>
              <div className="sub-title">
                <strong>Email:</strong> {email}
              </div>
              <div className="sub-title">
                <strong>Phone:</strong> {phoneNumber}
              </div>
            </div>
            <div className="modal-buttons">
              <button className="confirm-btn" onClick={handleProfileUpdate}>
                Yes, Update Profile
              </button>
              <button className="cancel-btn" onClick={handleCancelModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Picture Confirmation Modal */}
      {showDeletePicModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="title">Delete Profile Picture</div>
            <div className="sub-title">
              Are you sure you want to remove your profile picture?
            </div>
            <div className="modal-buttons">
              <button
                className="confirm-btn"
                onClick={handleConfirmDeletePicture}
              >
                Yes, Delete Picture
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancelDeletePicture}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
};

function getFirstAndLastName(userName) {
  // Split the user name by space to separate the first and last names
  const nameParts = userName.split(" ");

  // Check if the split array has at least two elements (first name and last name)
  if (nameParts.length >= 2) {
    const firstName = nameParts[0];
    const lastName = nameParts[1];
    return { firstName, lastName };
  } else {
    return { firstName: nameParts[0], lastName: null };
  }
}

export default MidSectionProfilePc;
